import { useRouteContext } from "@tanstack/react-router";

import { graphql } from "@repo/graphql-types";
import CalendarTime from "@repo/icons/default/CalendarTime";
import Clock from "@repo/icons/default/Clock";
import Door from "@repo/icons/default/Door";
import Link from "@repo/icons/default/Link";

import { ReservedScheduleTermsOfUseUrl } from "@/components/reserved-schedules/reserved-schedule-terms-of-use-url";
import { Button } from "@/components/ui/button/button";
import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from "@/components/ui/drawer/drawer";
import { useGraphQLMutationWithErrorHandler, useResetQuery } from "@/hooks/use-graphql";
import {
  minimumBookingDescription,
  pricingDescription,
  type ReservedScheduleModalityRules,
} from "@/lib/reserved-schedules/reserved-schedule-modality-rules";

import type { JSX } from "react";

/**
 * Todos os termos de uso ativos do membro, **sem filtrar por tipo**.
 *
 * O filtro saiu porque a checagem mudou de lugar: ela era um `redirect` no `loader` de `/create`,
 * onde só existia a pré-seleção, e passou a ser um gate no `handleSubmit`, onde existe a categoria
 * escolhida. Buscar por tipo obrigaria a disparar uma query no meio do submit e esperar por ela para
 * decidir se navega; com a lista inteira em mão, o gate é um `some()` síncrono.
 *
 * São poucas linhas por usuário — um termo por tipo, no máximo oito.
 */
export const HasUserAcceptedTermsOfUseQuery = graphql(/* GraphQL */ `
  query HasUserAcceptedTermsOfUseQuery {
    tbUsuariosTermosDeUso(where: { ativo: { _eq: true } }) {
      codTipoTermoDeUso
    }
  }
`);

export const UpsertTermsOfUseMutation = graphql(/* GraphQL */ `
  mutation UpsertTermsOfUseMutation(
    $IP: String!
    $codTipoTermoDeUso: Int!
    $codUsuario: Int!
    $dataUltimaAtualizacao: datetime2!
    $ativo: Boolean = true
  ) {
    insert_tbUsuariosTermosDeUso_one(
      object: {
        IP: $IP
        ativo: $ativo
        codTipoTermoDeUso: $codTipoTermoDeUso
        codUsuario: $codUsuario
        dataCadastro: $dataUltimaAtualizacao
        dataUltimaAtualizacao: $dataUltimaAtualizacao
      }
      if_matched: {
        match_columns: [codUsuario, codTipoTermoDeUso]
        update_columns: [dataUltimaAtualizacao, ativo, IP]
      }
    ) {
      codTermosDeUso
      codUsuario
      codTipoTermoDeUso
      dataCadastro
      dataUltimaAtualizacao
      ativo
    }
  }
`);

interface ReservedScheduleTermsOfUseDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /**
   * A categoria **escolhida** no formulário, não a pré-seleção: é o termo dela que o membro assina,
   * e os três blocos abaixo descrevem as regras dela.
   */
  modality: number;
  /** Regras da categoria escolhida, já resolvidas pelo formulário contra o catálogo (KRA-523). */
  rules: ReservedScheduleModalityRules;
  /**
   * O `codTipoTermoDeUso` que o aceite grava. Vem pronto de quem abriu o drawer, em vez de ser
   * derivado de `modality` aqui, para a regra de "qual termo pertence a qual categoria" existir num
   * lugar só — e para este componente não precisar de um ramo para o valor que não mapeia.
   *
   * `null` ⇔ drawer fechado. O componente fica **sempre montado** e dirigido por `open`, como o
   * resto do app faz com `DrawerRoot` — desmontar ao fechar pulava a animação de saída do vaul. Com
   * o drawer fechado o botão de aceite não é alcançável, então o `null` nunca chega à mutation.
   */
  termsOfUseType: number | null;
  memberIP: string;
  /** Chamado depois do aceite gravado. Fechar o drawer sem aceitar não dispara nada. */
  onAccepted: () => void;
}

/**
 * Aceite do termo de uso da categoria escolhida, pedido **no submit do formulário**.
 *
 * ## Por que drawer e não rota
 *
 * O gate era a rota `/settings/reserved-schedules/terms-of-use`, com `redirect` no `loader` de
 * `/create`. Duas coisas o inviabilizaram:
 *
 * 1. **No loader a categoria ainda não foi escolhida.** O gate consultava o termo da pré-seleção,
 *    então quem aceitou só o termo da parceria podia escolher a convencional e criar um PG cujo
 *    termo nunca assinou.
 * 2. **`reserved-schedule-confirm` voltava com `router.history.go(-5)`**, contagem fixa. Inserir uma
 *    entrada de histórico entre `/create` e `/create/slots` faria o fechamento do drawer de sucesso
 *    cair no lugar errado. (Hoje a volta é pelo índice gravado — `useReturnToWizardStop` —, mas
 *    uma rota a mais continuaria sendo uma tela a mais para o membro atravessar no voltar.)
 *
 * Cancelar não perde nada: o rascunho já foi gravado no atom antes de o drawer abrir, então o
 * formulário volta preenchido.
 */
export const ReservedScheduleTermsOfUseDrawer = ({
  open,
  setOpen,
  modality,
  rules,
  termsOfUseType,
  memberIP,
  onAccepted,
}: ReservedScheduleTermsOfUseDrawerProps): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });
  const { mutate, isPending } = useGraphQLMutationWithErrorHandler(
    UpsertTermsOfUseMutation,
  );
  const resetAcceptedTermsOfUse = useResetQuery(HasUserAcceptedTermsOfUseQuery);

  const acceptTermsOfUse = (): void => {
    if (termsOfUseType === null) {
      return;
    }

    mutate(
      {
        IP: memberIP,
        codUsuario: user.codUsuario,
        codTipoTermoDeUso: termsOfUseType,
        dataUltimaAtualizacao: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          // O aceite acabou de mudar o que a query devolve, e ela alimenta o gate deste drawer na
          // próxima entrada no fluxo. Sem resetar, o membro reveria o drawer de um termo que já
          // assinou.
          resetAcceptedTermsOfUse();
          onAccepted();
        },
      },
    );
  };

  return (
    <DrawerRoot open={open} setOpen={setOpen}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Termos de uso</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <div className="flex flex-col gap-2 pb-6">
            <span>
              O período garantido tem termos de uso distintos do modelo padrão da Livance.
            </span>
            <span>Confira abaixo.</span>
          </div>
          <ul className="flex flex-col gap-6">
            <li className="flex gap-2">
              <div className="bg-primary-50 h-[40px] w-[40px] p-2 rounded-full self-center">
                <Door className="fill-primary-400" size={24} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">Modelo de precificação</span>
                <span className="text-sm">{pricingDescription(rules)}</span>
              </div>
            </li>
            <li className="flex gap-2">
              <div className="bg-primary-50 h-[40px] w-[40px] p-2 rounded-full self-center">
                <CalendarTime className="fill-primary-400" size={24} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">Cancelamento antecipado</span>
                <span className="text-sm">
                  Cancele um dia pontual do período sem custo com uma antecedência mínima
                  de 7 dias.
                </span>
              </div>
            </li>
            <li className="flex gap-2">
              <div className="bg-primary-50 h-[40px] w-[40px] p-2 rounded-full self-center">
                <Clock className="fill-primary-400" size={24} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">Tempo mínimo de reserva</span>
                <span className="text-sm">{minimumBookingDescription(rules)}</span>
              </div>
            </li>
            <li className="text-left text-sm flex flex-row gap-1 items-center font-medium">
              <ReservedScheduleTermsOfUseUrl reservedScheduleType={modality} />
              <Link size={16} className="fill-[#0080a5]" />
            </li>
          </ul>
        </DrawerBody>
        <DrawerFooter>
          <div data-testid="submit-terms-button" className="w-full pt-6">
            <Button onClick={acceptTermsOfUse} loading={isPending} fullWidth>
              Li e aceito os termos
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </DrawerRoot>
  );
};
